// packages/shared/src/utils/caseBillingCoverage.ts
// Which insurance an episode (case) bills to. Frontend twin of the Xano function
// `resolve_case_billing_coverage.xs` — keep the rules identical in both places.
//
// case.billing_coverage values:
//   absent / null / "inherit"        -> the patient's primary insurance
//   "self_pay"                        -> a cash episode
//   "payer:<organization_payer_id>"   -> the patient insurance entry stamped with that org payer
//
// New policy:<insurance_entry_id> handles use a stable policy UUID without payer mapping.
// Legacy entries have no stable id of their own; `organization_payer_id` is the
// handle a case can point at. If that entry has since been removed from the patient,
// the resolver falls back to the primary and flags `unresolved`.
//
// Entries that lack `organization_payer_id` (synced before stamping existed, or whose
// PM insurance has not changed since) are still selectable in the case form: the form
// matches them against the org's active payers with the same uniqueness rules as the
// Xano function `resolve_insurance_org_payer_ids.xs`, stamps the id on the patient
// record at case-save time, and then saves the case as `payer:<id>` (link-on-save).
//
// When that match is not unique (several active payers share a name, common for large
// workers' comp and auto carriers) or finds nothing, the option is `unlinked` and carries
// the candidate payers; the form shows them and the user picks, which becomes the pending
// link. The matcher itself never guesses.

import { isSelfPayPlan } from '../constants/insurance';

export type BillingCoverageMode = 'insurance' | 'self_pay';
export type BillingCoverageSource = 'case' | 'primary' | 'fallback';

export interface CoverageInsuranceEntry {
  /** Stable patient policy identity, independent of payer mapping. */
  insurance_entry_id?: string;
  payer_name?: string;
  plan_name?: string;
  plan_type?: string;
  is_primary?: boolean;
  organization_payer_id?: string | null;
  claimmd_payer_id?: string | null;
  member_id?: string | null;
  /** Payer requires a physician referral on file (drives the case / RTB / billing-review alerts). */
  requires_referral?: boolean;
  /** Only meaningful with requires_referral: the referral must come from the patient's PCP. */
  requires_pcp_referral?: boolean;
  [key: string]: any;
}

export interface ResolvedBillingCoverage {
  mode: BillingCoverageMode;
  source: BillingCoverageSource;
  insurance: CoverageInsuranceEntry | null;
  unresolved: boolean;
}

/** Minimal shape of an organization_payer row the matcher needs. */
export interface OrgPayerLike {
  id: string;
  claimmd_payer_id?: string | null;
  display_name?: string | null;
  [key: string]: any;
}

/** An unlinked entry that resolved to exactly one org payer; the form stamps it on save. */
export interface PendingPayerLink {
  entryIndex: number;
  organizationPayerId: string;
  payerDisplayName: string;
}

export interface CoverageOption {
  value: string;
  label: string;
  /** Transient selection; assign a UUID before persisting the case handle. */
  pendingPolicyIndex?: number;
  disabled?: boolean;
  /** Set when choosing this option requires stamping organization_payer_id on the patient first. */
  pendingLink?: PendingPayerLink;
  /** Set when the entry has no organization_payer_id and no unique org payer match; the form must collect a choice before saving. */
  unlinked?: boolean;
  /** The entry's payer name, for notices about an unlinked option. */
  payerName?: string;
  /** Index into patient.insurance for an unlinked option (where the chosen id gets stamped). */
  entryIndex?: number;
  /** Org payers that matched but not uniquely; empty when nothing matched at all. */
  candidates?: OrgPayerLike[];
}

type CaseLike = { billing_coverage?: string | null } | null | undefined;
type PatientLike = { insurance?: CoverageInsuranceEntry[] | null } | null | undefined;

const SELF_PAY_ENTRY: CoverageInsuranceEntry = {
  payer_name: 'Self Pay',
  plan_type: 'self_pay',
  is_primary: true,
};

function primaryOf(rows: CoverageInsuranceEntry[]): CoverageInsuranceEntry | null {
  return rows.find(r => r?.is_primary) || rows[0] || null;
}

export function resolveCaseBillingCoverage(caseRow: CaseLike, patient: PatientLike): ResolvedBillingCoverage {
  const setting = caseRow?.billing_coverage || 'inherit';
  const rows: CoverageInsuranceEntry[] = Array.isArray(patient?.insurance) ? patient.insurance : [];
  const primary = primaryOf(rows);

  if (setting === 'self_pay') {
    return {
      mode: 'self_pay',
      source: 'case',
      insurance: rows.find(r => isSelfPayPlan(r)) || SELF_PAY_ENTRY,
      unresolved: false,
    };
  }

  if (setting.startsWith('payer:') || setting.startsWith('policy:')) {
    const policy = setting.startsWith('policy:');
    const id = setting.slice(policy ? 'policy:'.length : 'payer:'.length);
    const matches = id ? rows.filter(r => (policy ? r?.insurance_entry_id : r?.organization_payer_id) === id) : [];
    const match = policy ? (matches.length === 1 ? matches[0] : undefined) : matches[0];
    if (match) {
      return {
        mode: isSelfPayPlan(match) ? 'self_pay' : 'insurance',
        source: 'case',
        insurance: match,
        unresolved: false,
      };
    }
    return {
      mode: isSelfPayPlan(primary) ? 'self_pay' : 'insurance',
      source: 'fallback',
      insurance: primary,
      unresolved: true,
    };
  }

  return {
    mode: isSelfPayPlan(primary) ? 'self_pay' : 'insurance',
    source: 'primary',
    insurance: primary,
    unresolved: false,
  };
}

/** Short label for chips and summaries. */
export function describeBillingCoverage(resolved: ResolvedBillingCoverage | null | undefined): string {
  if (!resolved) return 'No insurance';
  if (resolved.source === 'case') {
    if (resolved.mode === 'self_pay') return 'Self pay (episode)';
    return `${resolved.insurance?.payer_name || 'Unknown payer'} (episode)`;
  }
  if (!resolved.insurance) return 'No insurance';
  return `Primary: ${resolved.insurance.payer_name || 'Unknown payer'}`;
}

export interface MissingReferral {
  /** The referral must come from the patient's primary care provider (gatekeeper plans). */
  pcp: boolean;
  payerName: string;
  /** Coverage fell back to the primary because the case's payer is no longer on the patient. */
  unresolved: boolean;
}

type ReferralCaseLike =
  | { billing_coverage?: string | null; referring_provider_id?: string | number | null }
  | null
  | undefined;

/**
 * Display-only, no Xano twin. Null when nothing is missing: no case row, the case already
 * has a referring provider, self-pay coverage, no insurance, or the billing payer does not
 * require a referral. `pcp` is only true when both entry flags are set, so a stale
 * `requires_pcp_referral` without `requires_referral` is harmless.
 */
export function resolveMissingReferral(caseRow: ReferralCaseLike, patient: PatientLike): MissingReferral | null {
  if (!caseRow || caseRow.referring_provider_id) return null;
  const resolved = resolveCaseBillingCoverage(caseRow, patient);
  const ins = resolved.insurance;
  if (resolved.mode !== 'insurance' || !ins?.requires_referral) return null;
  return {
    pcp: !!ins.requires_pcp_referral,
    payerName: ins.payer_name || 'This payer',
    unresolved: resolved.unresolved,
  };
}

// Matching rules ported verbatim from the lambda in resolve_insurance_org_payer_ids.xs
// so the form links an entry to exactly the payer the server would have stamped.
const normPayerName = (s: unknown): string => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const payerTokens = (s: unknown): string[] => String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
const payerNamesAgree = (a: unknown, b: unknown): boolean => {
  const ta = payerTokens(a);
  const tb = new Set(payerTokens(b));
  return ta.some(t => tb.has(t));
};

/**
 * Resolve an insurance entry to a single active org payer, or null when the match is
 * not unique. Unique claimmd_payer_id match wins, unless its name shares no tokens with
 * the entry's payer name and a distinct unique exact-name match exists (an eligibility-
 * stamped claimmd id can point at the wrong org payer). Zero or multiple claimmd matches
 * fall back to a unique exact-normalized-name match.
 */
export function matchUniqueOrgPayer(
  entry: CoverageInsuranceEntry | null | undefined,
  payers: OrgPayerLike[] | null | undefined,
): OrgPayerLike | null {
  return matchOrgPayerCandidates(entry, payers).match;
}

export interface OrgPayerMatchResult {
  /** The unique match, or null when zero or several payers qualify. */
  match: OrgPayerLike | null;
  /** Every payer that matched by claimmd id or exact name (deduped, list order); empty when nothing matched. */
  candidates: OrgPayerLike[];
}

/**
 * Same rules as `matchUniqueOrgPayer`, plus the full candidate set so a form can ask
 * the user to disambiguate when the match is not unique.
 */
export function matchOrgPayerCandidates(
  entry: CoverageInsuranceEntry | null | undefined,
  payers: OrgPayerLike[] | null | undefined,
): OrgPayerMatchResult {
  if (!entry || !Array.isArray(payers) || payers.length === 0) return { match: null, candidates: [] };
  const claimmd = entry.claimmd_payer_id ? String(entry.claimmd_payer_id) : '';
  const idMatches = claimmd
    ? payers.filter(p => p?.claimmd_payer_id && String(p.claimmd_payer_id) === claimmd)
    : [];
  const nameNorm = normPayerName(entry.payer_name);
  const nameMatches = nameNorm
    ? payers.filter(p => normPayerName(p?.display_name) === nameNorm)
    : [];
  let match: OrgPayerLike | null = null;
  if (idMatches.length === 1) {
    match = idMatches[0];
    if (entry.payer_name && !payerNamesAgree(match.display_name, entry.payer_name) &&
        nameMatches.length === 1 && nameMatches[0].id !== match.id) {
      match = nameMatches[0];
    }
  } else if (nameMatches.length === 1) {
    match = nameMatches[0];
  }
  const seen = new Set<string>();
  const candidates: OrgPayerLike[] = [];
  for (const p of [...idMatches, ...nameMatches]) {
    const id = String(p.id);
    if (seen.has(id)) continue;
    seen.add(id);
    candidates.push(p);
  }
  return { match, candidates };
}

/**
 * Options for the case form's "Insurance for this case" select.
 * `orgPayers` is the org's active payer list; when supplied, entries missing
 * organization_payer_id that uniquely match a payer become selectable with a
 * `pendingLink` the form must stamp before saving the case. Entries with zero or
 * several matches are listed as `unlinked` with their `candidates`; the form collects
 * the user's choice and turns it into the pending link. Veradigm callers pass
 * allowUnmapped to select a policy without requiring a payer link; new identities
 * are stamped at save time, and the transient array position is never persisted.
 */
export function coverageOptionsForPatient(patient: PatientLike, orgPayers: OrgPayerLike[] = [], allowUnmapped = false): CoverageOption[] {
  const rows: CoverageInsuranceEntry[] = Array.isArray(patient?.insurance) ? patient.insurance : [];
  const options: CoverageOption[] = [
    { value: 'inherit', label: "Use patient's primary (default)" },
    { value: 'self_pay', label: 'Self pay' },
  ];
  // Two entries resolving to the same org payer would share a value; the case handle
  // is the payer id, so the second is redundant and would only break option keys.
  const seen = new Set<string>();
  rows.forEach((entry, index) => {
    const payerName = entry?.payer_name || 'Unknown payer';
    const label = entry?.plan_name ? `${payerName} (${entry.plan_name})` : payerName;
    if (entry?.insurance_entry_id || allowUnmapped) {
      const value = entry?.insurance_entry_id ? `policy:${entry.insurance_entry_id}` : `unmapped:${index}`;
      options.push({ value, label: entry?.member_id ? `${label} · ${entry.member_id}` : label, ...(entry?.insurance_entry_id ? {} : { pendingPolicyIndex: index }) });
      return;
    }
    if (entry?.organization_payer_id) {
      const value = `payer:${entry.organization_payer_id}`;
      if (seen.has(value)) return;
      seen.add(value);
      options.push({ value, label });
      return;
    }
    const { match, candidates } = matchOrgPayerCandidates(entry, orgPayers);
    if (match) {
      const value = `payer:${match.id}`;
      if (seen.has(value)) return;
      seen.add(value);
      options.push({
        value,
        label,
        pendingLink: {
          entryIndex: index,
          organizationPayerId: String(match.id),
          payerDisplayName: match.display_name || payerName,
        },
      });
    } else {
      options.push({
        value: `unlinked:${index}`,
        label: `${payerName} (choose a payer)`,
        unlinked: true,
        payerName,
        entryIndex: index,
        candidates,
      });
    }
  });
  return options;
}
